import { useCallback, useState } from 'react';
import FormGroup from '~/components/Form/FormGroup';
import PlainTextField from '~/components/Form/PlainTextField';
import Alert from '~/components/General/Alert';
import Button from '~/components/General/Button';
import Modal from '~/components/General/Modal';
import usePackageSessionSave from '~/hooks/bookingFlow/usePackageSessionSave';
import { useOptions } from '~/hooks/useOptions';
import useTranslation from '~/hooks/useTranslation';
import saveTripStyles from '~/styles/bookingFlow/save-trip.module.css';
import { PackageSessionSaveRequest } from '~/types/api';
import { InferredBookingFlowOptions } from '~/types/options';
import IconDeviceFloppy from '~icons/tabler/device-floppy';

type Props = {
  sessionId: string;
  className?: string;
};

export default function SaveTrip({
  sessionId,
  className,
}: Props) {
  const options = useOptions() as InferredBookingFlowOptions;
  const [modalOpen, setModalOpen] = useState(false);
  const translate = useTranslation();

  const [formData, setFormData] = useState<PackageSessionSaveRequest>({
    order_session_id: sessionId,
    office_id: options.officeId,
    department_id: options.departmentId,
    locale: options.locale,
    url: window.location.href,
    email: '',
    name: '',
  });

  const packageSessionSaveMutation = usePackageSessionSave();

  const onOpenModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const onSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    packageSessionSaveMutation.mutate(formData);
  }, [formData, packageSessionSaveMutation]);

  const onInputChange = useCallback((key: string, value: string) => {
    setFormData({ ...formData, [key]: value });
  }, [formData]);

  return (
    <div className={className}>
      <Button
        onClick={onOpenModal}
        type="button"
        styleType="outlineSecondary"
        className={saveTripStyles.saveTripButton}
      >
        <IconDeviceFloppy className={saveTripStyles.saveTripIcon} />
        <span>
          {translate('bookingFlow.overview.save_trip_button')}
        </span>
      </Button>

      <Modal
        title={translate('bookingFlow.overview.save_trip_modal_title')}
        description={translate('bookingFlow.overview.save_trip_modal_description')}
        isOpen={modalOpen}
        onClose={onCloseModal}
      >
        {
          packageSessionSaveMutation.isSuccess
            ? (
              <div className={saveTripStyles.saveTripSuccess}>
                {translate('bookingFlow.overview.save_trip_modal_success')}
              </div>
            )
            : (
              <>
                <Alert
                  type="danger"
                  error={packageSessionSaveMutation.error}
                />

                <form method="GET" action="" onSubmit={onSubmit}>
                  <FormGroup>
                    <PlainTextField
                      label={translate('form.name')}
                      value={formData.name}
                      onChange={(event) => onInputChange('name', event.target.value)}
                      max={200}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <PlainTextField
                      label={translate('form.email')}
                      fieldType="email"
                      value={formData.email}
                      onChange={(event) => onInputChange('email', event.target.value)}
                      max={200}
                      required
                    />
                  </FormGroup>

                  <Button
                    type="submit"
                    styleType="primary"
                    disabled={packageSessionSaveMutation.isPending}
                  >
                    {translate('bookingFlow.overview.save_trip_modal_submit')}
                  </Button>
                </form>
              </>
            )
        }
      </Modal>
    </div>
  );
}
